//built-in libraries
import { useState } from 'react';

//Third party libraries
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';

//Custom components
import Button from '../../../UI/Button';
import { HookFormProvider } from '../../../UI/HookForm/Provider';
import { HookForm } from '../../../UI/HookForm/Form';
import { HookInput } from '../../../UI/HookForm/Form/Input';

//services
import { fetchAccountTitles } from '../../../../api/accounts';
import { sendInvite } from '../../../../api/user';
import { notifyUser } from '../../../../store/Toast';
import { authSelector } from '../../../../store';

//Assets and Miscellaneous
import { emailRegEx } from '../../../../utils/RegularExpressions';
import { NewUserInviteType } from '../../../../types/User';
import { ListTitlesType, TitleType } from '../../../../types/role';
import { HookReactSingleSelect } from '../../../UI/HookForm/Form/ReactSelect';
import { sanitizeResponseError } from '../../../../utils';

//Component Begin
export default function SingleUserInviteForm({
  formModalHandler,
}: {
  formModalHandler: (modalName: string) => void;
}) {
  const dispatch = useDispatch();
  const { data: currentUserData } = useSelector(authSelector);
  const getOrgTitles = useQuery(['org-titles'], () => fetchAccountTitles(), {
    select: (data: { data: ListTitlesType[] }) => {
      const transformedOrgTitles: ListTitlesType[] = [];
      data.data.forEach((title: ListTitlesType) => {
        if (currentUserData.roleValue !== 'SUPER_ADMIN') {
          if (title.roleId !== 'SUPER_ADMIN' && title.roleId !== 'ADMIN') {
            transformedOrgTitles.push(title);
          }
        } else {
          transformedOrgTitles.push(title);
        }
      });
      return { data: transformedOrgTitles };
    },
  });
  const orgTitles = getOrgTitles.data?.data;

  const validationSchema = Yup.object({
    name: Yup.string()
      .required('Full Name is required')
      .min(2, 'Full Name must be greater than 2 characters')
      .max(25, 'Full Name must be less than 25 characters'),
    email: Yup.string()
      .required('Email is required')
      .matches(emailRegEx, 'Invalid Email Address')
      .max(250, 'Invalid Email Address'),
    titleId: Yup.object({
      name: Yup.string(),
      id: Yup.number(),
    })
      .required('Organization title is required')
      .oneOf(orgTitles || [], 'You must select a valid option'),
  });

  const queryClient = useQueryClient();
  const inviteUser = useMutation(
    (payload: NewUserInviteType) => sendInvite(payload),
    {
      onSuccess: () => userInviteSuccess(),
      onError: (error: any) => userInviteError(error),
    }
  );

  const userInviteSuccess = () => {
    queryClient.invalidateQueries(['users']);
    dispatch(
      notifyUser({
        showToast: true,
        toastMessage: 'User Invite successful',
        toastType: 'success',
      })
    );
    formModalHandler('inviteNewUser');
  };

  const userInviteError = (error: any) => {
    const resErrorMsg = sanitizeResponseError(error);
    dispatch(
      notifyUser({
        showToast: true,
        toastMessage: resErrorMsg,
        toastType: 'failed',
      })
    );
  };

  const onSubmit = (data: {
    name: string;
    email: string;
    titleId: TitleType;
  }) => {
    inviteUser.mutate({
      ...data,
      titleId: parseInt(data.titleId.id.toString()),
    });
  };

  return (
    <>
      <HookFormProvider resolver={yupResolver(validationSchema)} mode='all'>
        <HookForm onSubmit={onSubmit}>
          <section className='space-y-5 my-5'>
            <HookInput id='name' name='name' type='text' label='Full Name' />
            <HookInput
              id='email'
              name='email'
              type='text'
              label='E-mail'
              placeholder='john.doe@example.com'
            />
            <HookReactSingleSelect
              id='titleId'
              options={orgTitles}
              getOptionLabel={(option) => (option as TitleType).name}
              getOptionValue={(option) => (option as TitleType).id.toString()}
              placeholder='Select Title'
              name='titleId'
              label='Organizational Title'
            />
          </section>
          <div className='flex gap-2 items-center justify-end '>
            <Button
              label='Cancel'
              btnStyle='btn-neutral'
              type='button'
              disabled={inviteUser.isLoading}
              onClick={() => formModalHandler('inviteNewUser')}
            />
            <Button
              label={`${
                inviteUser.isLoading ? 'Sending Invite...' : 'Send Invite'
              }`}
              btnStyle='btn-primary'
              type='submit'
              disabled={inviteUser.isLoading}
            />
          </div>
        </HookForm>
      </HookFormProvider>
    </>
  );
}
